import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { TextField, InputAdornment, IconButton, CircularProgress } from "@mui/material";
import React, { useCallback, useRef, useState, memo } from "react";

interface ClientSearchProps {
  onSearch: (term: string) => Promise<void>;
  defaultPageSize: number;
}

const ClientSearch = memo(({ onSearch, defaultPageSize }: ClientSearchProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastSearchTerm = useRef(searchValue);

  const performSearch = useCallback(
    async (term: string) => {
      if (term === lastSearchTerm.current) {
        return;
      }

      setIsSearching(true);
      lastSearchTerm.current = term;

      try {
        await onSearch(term);
      } finally {
        setIsSearching(false);
      }
    },
    [onSearch]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setSearchValue(newValue);

      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }

      searchTimeout.current = setTimeout(() => {
        performSearch(newValue);
      }, 300);
    },
    [performSearch]
  );

  const handleSearchClear = useCallback(() => {
    setSearchValue("");
    performSearch("");
  }, [performSearch]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  return (
    <TextField
      size="small"
      variant="outlined"
      placeholder="Cerca clienti..."
      value={searchValue}
      onChange={handleSearchChange}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          handleSearchClear();
        }
      }}
      sx={{ minWidth: 240 }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {isSearching ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
          </InputAdornment>
        ),
        endAdornment: searchValue ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleSearchClear} edge="end" disabled={isSearching}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
});

ClientSearch.displayName = "ClientSearch";

export default ClientSearch;
